import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import withRouter from "../withRouter.js";
import { clientname, currentTenant, logo, userid } from "../Common/Credentials.js";
import { spin } from "../Common/NewLoader.jsx";
import { assetmanagementid } from "../Common/Permissions.js";
import Footer from '../Component/Footer';
import Header from '../Component/Header';
import Menu from '../Component/Menu';
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AssetsService from "./AssetsService.js";
import moment from "moment";

const ProductionUsage = (props) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'startdate', direction: 'desc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshData = useCallback(() => {
    setLoading(true);
    AssetsService.GetProductionusageoftTnant(currentTenant)
      .then(response => {
        const sortedData = response.data.sort((a, b) =>
          new Date(b.startdate) - new Date(a.startdate)
        );
        setData(sortedData);
        setFilteredData(sortedData);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: "Your Session is Lost: Please login Back to resume",
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate("/"));
        }
        setLoading(false);
      });
  }, [currentTenant, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshData();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshData]);

  useEffect(() => {
    updateFilteredData();
  }, [data, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      // Handle date sorting
      if (sortConfig.key === 'startdate' || sortConfig.key === 'enddate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = data;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = data.filter(item =>
        item.assetid?.toString().toLowerCase().includes(lowerSearchTerm) ||
        item.warehouse?.toLowerCase().includes(lowerSearchTerm) ||
        item.workcenter?.toLowerCase().includes(lowerSearchTerm) ||
        item.joid?.toString().toLowerCase().includes(lowerSearchTerm) ||
        item.usagetype?.toLowerCase().includes(lowerSearchTerm) ||
        item.createdby?.toLowerCase().includes(lowerSearchTerm) ||
        moment(item.startdate).format('DD-MM-YYYY').toLowerCase().includes(lowerSearchTerm) ||
        moment(item.enddate).format('DD-MM-YYYY').toLowerCase().includes(lowerSearchTerm) ||
        item.status?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredData);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleTerminate = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to terminate this production usage?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#152f5f',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, terminate it!'
    }).then((result) => {
      if (result.isConfirmed) {
        AssetsService.updateproductionusagestatus(id)
          .then(() => {
            refreshData();
            Swal.fire(
              'Terminated!',
              'Production usage has been terminated.',
              'success'
            );
          });
      }
    });
  };

  const generatePDF = () => {
    const pdfData = filteredData.map(item => ({
      ...item,
      startdate: moment(item.startdate).format("DD-MM-YYYY"),
      enddate: moment(item.enddate).format("DD-MM-YYYY"),
      fromtime: moment(item.fromtime).format("HH:mm"),
      totime: moment(item.totime).format("HH:mm")
    }));

    const columns = [
      { header: "Asset Id", dataKey: 'assetid' },
      { header: "Warehouse", dataKey: 'warehouse' },
      { header: "Workcenter", dataKey: 'workcenter' },
      { header: "Job Order", dataKey: 'joid' },
      { header: "Usage Type", dataKey: 'usagetype' },
      { header: "Start Date", dataKey: "startdate" },
      { header: "End Date", dataKey: "enddate" },
      { header: "From Time", dataKey: 'fromtime' },
      { header: "To Time", dataKey: 'totime' },
      { header: "Created By", dataKey: 'createdby' },
      { header: "Status", dataKey: 'status' }
    ];

    const doc = new jsPDF('p', 'pt');

    // Add logo
    if (logo) {
      doc.addImage(logo, "GIF", 40, 20, 60, 60);
    }

    // Add company name
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`${clientname}`, 180, 40);

    // Add report title
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text(`Production Usage Report`, 180, 80);

    // Add timestamp
    doc.setFontSize(10);
    doc.text(`${moment().format("DD-MM-YYYY HH:mm")}`, 500, 80);

    // Add line separator
    doc.line(40, 90, 555, 90);

    // Add table
    autoTable(doc, {
      theme: 'grid',
      startY: 100,
      margin: { left: 40, right: 40 },
      headStyles: { fillColor: [21, 47, 95] },
      body: pdfData,
      columns: columns,
      styles: { fontSize: 8 },
      headStyles: { fontSize: 9, fontStyle: 'bold' }
    });

    doc.save(`Production_Usage_Report_${moment().format('DDMMYYYY_HHmm')}.pdf`);
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredData.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredData.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No production usage records found matching your search' : 'No production usage records available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'assetid' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('assetid')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Asset Id
                    <SortIndicator columnKey="assetid" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'warehouse' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('warehouse')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Warehouse
                    <SortIndicator columnKey="warehouse" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'workcenter' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('workcenter')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Workcenter
                    <SortIndicator columnKey="workcenter" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'joid' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('joid')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Job Order
                    <SortIndicator columnKey="joid" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'usagetype' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('usagetype')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Usage Type
                    <SortIndicator columnKey="usagetype" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'startdate' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('startdate')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Start Date
                    <SortIndicator columnKey="startdate" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'enddate' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('enddate')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    End Date
                    <SortIndicator columnKey="enddate" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'fromtime' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('fromtime')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    From Time
                    <SortIndicator columnKey="fromtime" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'totime' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('totime')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    To Time
                    <SortIndicator columnKey="totime" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'createdby' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('createdby')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Created By
                    <SortIndicator columnKey="createdby" />
                  </div>
                </th>
                <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((item) => (
                <tr
                  key={item.id}
                  className={`UI-table-row ${hoveredRow === item.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                  onMouseEnter={() => setHoveredRow(item.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* Asset ID Cell */}
                  <td
                    className={`UI-td UI-service-name ${hoveredRow === item.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.assetid, searchTerm)}
                  </td>

                  {/* Warehouse Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.warehouse, searchTerm)}
                  </td>

                  {/* Workcenter Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.workcenter, searchTerm)}
                  </td>

                  {/* Job Order Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.joid, searchTerm)}
                  </td>

                  {/* Usage Type Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    <span className={`UI-status-badge ${item.usagetype === 'Production' ? 'UI-status-active' : 'UI-status-inactive'}`}>
                      {item.usagetype}
                    </span>
                  </td>

                  {/* Start Date Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {moment(item.startdate).format('DD-MM-YYYY')}
                  </td>

                  {/* End Date Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {moment(item.enddate).format('DD-MM-YYYY')}
                  </td>

                  {/* From Time Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {moment(item.fromtime).format('HH:mm')}
                  </td>

                  {/* To Time Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {moment(item.totime).format('HH:mm')}
                  </td>

                  {/* Created By Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.createdby, searchTerm)}
                  </td>

                  {/* Action Cell */}
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                    <div className="UI-action-buttons">
                      {/* Edit Button - Commented as per original */}
                      {/* <Tooltip title="Edit" arrow>
                        <Link
                          as="button"
                          className="fa fa-edit textColour mr-2 UI-action-button"
                          to={{
                            pathname: "/newproductionusage",
                            state: {
                              id: item.id,
                            },
                          }}
                        ></Link>
                      </Tooltip> */}

                      {/* Terminate Button */}
                      {item.status !== "Available" && (assetmanagementid === "2" || assetmanagementid === "3") && (
                        <Tooltip title="Terminate" arrow>
                          <button
                            type="button"
                            className={`UI-action-button UI-terminate-button ${hoveredAction === `terminate-${item.id}` ? 'UI-terminate-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onClick={() => handleTerminate(item.id)}
                            onMouseEnter={() => setHoveredAction(`terminate-${item.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                          >
                            <i className={`fa fa-times UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `terminate-${item.id}` ? 'UI-action-icon-hover' : ''}`} />
                            {isMobile ? "" : "Terminate"}
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
          <div className="UI-card">
            {/* Header */}
            <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
              <nav className={`UI-breadcrumb ${isMobile ? 'UI-breadcrumb-mobile' : ''}`}>
                <Link
                  to="/assetmanagement"
                  className={`UI-breadcrumb-item ${hovered === 'home' ? 'UI-breadcrumb-item-hover' : ''}`}
                  onMouseEnter={() => setHovered('home')}
                  onMouseLeave={() => setHovered(null)}
                >
                  Home
                </Link>
                <span>/</span>
                <span className="UI-breadcrumb-item UI-breadcrumb-active">
                  Production Usage
                </span>
              </nav>

              <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                <div>
                  <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                    Production Usage
                  </h1>
                  <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                    Manage production usage records
                  </p>
                </div>

                <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                  {/* Mobile Search */}
                  {isMobile && (
                    <input
                      type="text"
                      placeholder="Search production usage..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="UI-mobile-search-input"
                    />
                  )}

                  <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                    {/* Desktop Search Toggle */}
                    {!isMobile && (
                      <div
                        className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                        onClick={() => {
                          if (!showSearch) setShowSearch(true);
                        }}
                        onMouseEnter={() => setHovered("search")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i
                          className="fa fa-search UI-search-icon"
                        />
                        {!showSearch && "Search"}
                        {showSearch && (
                          <input
                            type="text"
                            placeholder="Search production usage..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="UI-search-input"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => {
                              if (searchTerm.trim() === "") {
                                setShowSearch(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* Export PDF Button */}
                    <button
                      className={`UI-btn UI-btn-secondary ${hovered === "pdf" ? 'UI-btn-secondary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                      onClick={generatePDF}
                      onMouseEnter={() => setHovered("pdf")}
                      onMouseLeave={() => setHovered(null)}
                    >
                      <i className="fa fa-file-pdf" /> {isMobile ? "PDF" : "Export PDF"}
                    </button>

                    {/* New Production Usage button */}
                    {(assetmanagementid === "2" || assetmanagementid === "3") && (
                      <Link
                        to="/newproductionusage"
                        state={{ id: -1 }}
                        className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                        onMouseEnter={() => setHovered("new")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i className="fa fa-plus" /> {isMobile ? "New" : "New Usage"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Message Display */}
            {/* {message && (
              <div className="text-center text-green">
                {message}
              </div>
            )} */}

            {loading ? (
              <div className="UI-loading-container">
                {spin}
              </div>
            ) : (
              renderTable()
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default withRouter(ProductionUsage);